import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { FilterKind, FilterVocabulary, PilotResult } from '@shared/services'
import { FILTER_KINDS, pickPilot, readFilterVocabulary, runPilot } from '@shared/services'

/**
 * Auto Filter -- the pilot (Huanrui, 2026-10-05), now the last part of the
 * page, folded away (AutoFilterPage)
 * -------------------------------------------------------------
 * The spec is the project doc wonderworks-auto-filter-spec.md. Before the
 * model writes a single tag, this page has it tag pictures Huanrui already
 * tagged by hand and lays the two side by side: how often it agrees, how
 * much of what is there it finds, what it would add where nothing was, what
 * new names it proposes, and what it costs. NOTHING IS WRITTEN to the
 * library; only the usage, so the pilot shows on the Usage page.
 *
 * The rest of the page in the spec (runs, pending tags, spot checks) comes
 * after the pilot has said the model is good enough.
 *
 * The last pilot is kept in this browser, so a reload does not throw away
 * what was paid for.
 */

const KEPT = 'ww-auto-filter-pilot-v1'
/** Pictures per request; the Worker asks about them at the same time. */
const CHUNK = 5

const LABELS: Record<FilterKind, string> = {
  type: 'Type',
  format: 'Format',
  material: 'Material',
  brand: 'Brand',
  product: 'Product',
  industry: 'Industry',
  other: 'Other',
}

interface Kept {
  at: string
  detail: 'low' | 'high'
  results: PilotResult[]
}

function readKept(): Kept | null {
  try {
    const raw = localStorage.getItem(KEPT)
    return raw ? (JSON.parse(raw) as Kept) : null
  } catch {
    return null
  }
}

function keep(value: Kept) {
  try {
    localStorage.setItem(KEPT, JSON.stringify(value))
  } catch {
    /* too large or refused: the results stay on screen until the page closes */
  }
}

const pct = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—')
const usd = (value: number) => (value >= 1 ? `$${value.toFixed(2)}` : value >= 0.01 ? `$${value.toFixed(3)}` : `$${value.toFixed(5)}`)

export function AutoFilterPilot() {
  const [vocab, setVocab] = useState<FilterVocabulary | null>(null)
  const [error, setError] = useState('')
  const [count, setCount] = useState(100)
  const [detail, setDetail] = useState<'low' | 'high'>(() => readKept()?.detail ?? 'low')
  const [results, setResults] = useState<PilotResult[]>(() => readKept()?.results ?? [])
  const [keptAt, setKeptAt] = useState(() => readKept()?.at ?? '')
  const [planned, setPlanned] = useState(0)
  const [running, setRunning] = useState(false)
  const [onlyDiffer, setOnlyDiffer] = useState(false)
  const stop = useRef(false)

  useEffect(() => {
    readFilterVocabulary()
      .then(setVocab)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not read the tags'))
  }, [])

  const nameOf = useMemo(() => {
    const map = new Map<string, string>()
    if (vocab) for (const kind of FILTER_KINDS) for (const tag of vocab.tags[kind]) map.set(tag.id, tag.name)
    return map
  }, [vocab])

  const start = useCallback(async () => {
    setError('')
    setRunning(true)
    stop.current = false
    const at = new Date().toISOString()
    let done: PilotResult[] = []
    setResults([])
    setKeptAt(at)
    try {
      const { assetIds } = await pickPilot(count)
      setPlanned(assetIds.length)
      for (let i = 0; i < assetIds.length && !stop.current; i += CHUNK) {
        const answered = await runPilot(assetIds.slice(i, i + CHUNK), detail)
        done = [...done, ...answered]
        setResults(done)
        keep({ at, detail, results: done })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The pilot stopped')
    } finally {
      setRunning(false)
    }
  }, [count, detail])

  /* ---------- what the pilot says ---------- */

  const answered = results.filter((one) => one.answer)
  const failed = results.filter((one) => !one.answer)

  const byKind = useMemo(
    () =>
      FILTER_KINDS.map((kind) => {
        let judged = 0 // pictures with a hand-made tag here, that the model answered
        let given = 0 // tags the model gave on those pictures
        let agreed = 0 // ... that are also the hand-made ones
        let manualTotal = 0
        let found = 0
        let filledEmpty = 0 // pictures with nothing here by hand, where the model said something
        let unclear = 0
        const proposed = new Map<string, number>()
        for (const one of answered) {
          const ai = one.answer!.kinds[kind]
          const manual = new Set(one.manual[kind])
          if (ai.unclear) unclear += 1
          for (const name of ai.proposed) proposed.set(name, (proposed.get(name) ?? 0) + 1)
          if (manual.size > 0) {
            judged += 1
            given += ai.tagIds.length + ai.proposed.length
            agreed += ai.tagIds.filter((id) => manual.has(id)).length
            manualTotal += manual.size
            found += [...manual].filter((id) => ai.tagIds.includes(id)).length
          } else if (ai.tagIds.length + ai.proposed.length > 0) {
            filledEmpty += 1
          }
        }
        return {
          kind,
          judged,
          precision: pct(agreed, given),
          recall: pct(found, manualTotal),
          filledEmpty,
          unclear,
          proposed: [...proposed.entries()].sort((a, b) => b[1] - a[1]),
        }
      }),
    [answered],
  )

  const cost = answered.reduce((sum, one) => sum + one.answer!.costUsd, 0)
  const tokens = answered.reduce(
    (sum, one) => ({
      input: sum.input + one.answer!.usage.input,
      cached: sum.cached + one.answer!.usage.cached,
      output: sum.output + one.answer!.usage.output,
    }),
    { input: 0, cached: 0, output: 0 },
  )
  const n = Math.max(1, answered.length)
  const seconds = answered.reduce((sum, one) => sum + one.answer!.ms, 0) / n / 1000

  const differs = (one: PilotResult) =>
    !one.answer ||
    FILTER_KINDS.some((kind) => {
      const manual = one.manual[kind]
      const ai = one.answer!.kinds[kind]
      return manual.length > 0 && (ai.proposed.length > 0 || manual.some((id) => !ai.tagIds.includes(id)) || ai.tagIds.some((id) => !manual.includes(id)))
    })
  const listed = onlyDiffer ? results.filter(differs) : results

  return (
    <div className="afl afl__pilot">
        <p className="afl__note afl__note--top">
          {vocab?.model ?? 'The model'} tags Audit pictures you have already tagged, and the two are compared.
          Pictures marked Non-Packaging are left out. Nothing is written to the library.
        </p>

      <section className="afl__card afl__run">
        <div className="afl__controls">
          <label className="afl__field">
            <span>Pictures</span>
            <select value={count} disabled={running} onChange={(event) => setCount(Number(event.target.value))}>
              {[20, 50, 100, 200].map((one) => (
                <option key={one} value={one}>
                  {one}
                </option>
              ))}
            </select>
          </label>
          <div className="afl__field">
            <span>Picture detail</span>
            <div className="afl__seg" role="group" aria-label="Picture detail">
              {(['low', 'high'] as const).map((one) => (
                <button key={one} type="button" aria-pressed={detail === one} disabled={running} onClick={() => setDetail(one)}>
                  {one === 'low' ? 'Low' : 'High'}
                </button>
              ))}
            </div>
          </div>
          <span className="afl__spacer" />
          {running ? (
            <button type="button" className="afl__btn" onClick={() => (stop.current = true)}>
              Stop
            </button>
          ) : (
            <button type="button" className="afl__btn afl__btn--go" disabled={!vocab} onClick={() => void start()}>
              Run the pilot
            </button>
          )}
        </div>
        {running || results.length > 0 ? (
          <div className="afl__progress">
            <div className="afl__track">
              <span style={{ width: `${planned ? Math.min(100, (results.length / planned) * 100) : 0}%` }} />
            </div>
            <span>
              {results.length}
              {planned ? ` of ${planned}` : ''} pictures · {usd(cost)}
              {failed.length ? ` · ${failed.length} failed` : ''}
              {!running && keptAt ? ` · ${new Date(keptAt).toLocaleString()}` : ''}
            </span>
          </div>
        ) : null}
        {error ? <p className="afl__error">{error}</p> : null}
        {vocab ? (
          <p className="afl__note">
            Existing tags:{' '}
            {FILTER_KINDS.map((kind) =>
              vocab.groups[kind] ? `${LABELS[kind]} ${vocab.tags[kind].length}` : `${LABELS[kind]} (no category)`,
            ).join(' · ')}
          </p>
        ) : null}
      </section>

      {answered.length > 0 ? (
        <>
          <section className="afl__card">
            <span className="afl__label">How it did</span>
            <table className="afl__table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th title="Pictures that have a tag here by hand">Judged on</th>
                  <th title="Of the tags it gave on those pictures, how many are the hand-made ones">Agrees</th>
                  <th title="Of the hand-made tags, how many it found">Finds</th>
                  <th title="Pictures with nothing here by hand, where it said something">Adds where empty</th>
                  <th title="Said it could not tell">Unclear</th>
                  <th>New names it proposed</th>
                </tr>
              </thead>
              <tbody>
                {byKind.map((row) => (
                  <tr key={row.kind}>
                    <td>{LABELS[row.kind]}</td>
                    <td>{row.judged}</td>
                    <td className="afl__num">{row.precision}</td>
                    <td className="afl__num">{row.recall}</td>
                    <td>{row.filledEmpty}</td>
                    <td>{row.unclear}</td>
                    <td className="afl__names">
                      {row.proposed.length === 0
                        ? '—'
                        : row.proposed.slice(0, 12).map(([name, times]) => (
                            <span key={name} className="afl__chip afl__chip--new">
                              {name} <em>{times}</em>
                            </span>
                          ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="afl__note">
              Per picture: {Math.round(tokens.input / n).toLocaleString()} tokens in ({Math.round(tokens.cached / n).toLocaleString()}{' '}
              from cache), {Math.round(tokens.output / n).toLocaleString()} out · {usd(cost / n)} · {seconds.toFixed(1)} s.
              At this rate 30,000 pictures would cost about {usd((cost / n) * 30000)}.
            </p>
          </section>

          <section className="afl__card">
            <div className="afl__list-head">
              <span className="afl__label">Picture by picture</span>
              <span className="afl__spacer" />
              <label className="afl__check">
                <input type="checkbox" checked={onlyDiffer} onChange={(event) => setOnlyDiffer(event.target.checked)} />
                Only where they differ
              </label>
            </div>
            <div className="afl__legend">
              <span className="afl__chip afl__chip--both">both</span>
              <span className="afl__chip afl__chip--hand">by hand only</span>
              <span className="afl__chip afl__chip--ai">model only</span>
              <span className="afl__chip afl__chip--new">new name</span>
            </div>
            <div className="afl__rows">
              {listed.map((one) => (
                <article key={one.assetId} className="afl__row">
                  <div className="afl__thumb">{one.thumb ? <img src={one.thumb} alt={one.title} loading="lazy" /> : null}</div>
                  <div className="afl__kinds">
                    {one.answer ? (
                      FILTER_KINDS.map((kind) => {
                        const manual = one.manual[kind]
                        const ai = one.answer!.kinds[kind]
                        const both = manual.filter((id) => ai.tagIds.includes(id))
                        const handOnly = manual.filter((id) => !ai.tagIds.includes(id))
                        const aiOnly = ai.tagIds.filter((id) => !manual.includes(id))
                        return (
                          <div key={kind} className="afl__kind" title={ai.evidence}>
                            <span className="afl__kind-name">{LABELS[kind]}</span>
                            {both.map((id) => (
                              <span key={id} className="afl__chip afl__chip--both">{nameOf.get(id) ?? id}</span>
                            ))}
                            {handOnly.map((id) => (
                              <span key={id} className="afl__chip afl__chip--hand">{nameOf.get(id) ?? id}</span>
                            ))}
                            {aiOnly.map((id) => (
                              <span key={id} className="afl__chip afl__chip--ai">{nameOf.get(id) ?? id}</span>
                            ))}
                            {ai.proposed.map((name) => (
                              <span key={name} className="afl__chip afl__chip--new">{name}</span>
                            ))}
                            {ai.unclear ? <span className="afl__unclear">unclear</span> : null}
                            {ai.evidence ? <span className="afl__evidence">{ai.evidence}</span> : null}
                          </div>
                        )
                      })
                    ) : (
                      <p className="afl__error">{one.error || 'No answer'}</p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>
        </>
      ) : null}
    </div>
  )
}
